import { z } from "zod";
import {
  MarketplaceFulfillmentStatusSchema,
  type MarketplaceFulfillmentStatus,
  ACTIVE_PIPELINE_FULFILLMENT_STATUSES,
  ARCHIVED_FULFILLMENT_STATUSES,
  HISTORY_FULFILLMENT_STATUSES,
  TERMINAL_FULFILLMENT_STATUSES,
  isActivePipelineFulfillmentStatus,
  requiresProductionScheduling,
  usesNewOrderHandoffEmail,
} from "./marketplace-fulfillment.js";

export {
  MarketplaceFulfillmentStatusSchema,
  type MarketplaceFulfillmentStatus,
  ACTIVE_PIPELINE_FULFILLMENT_STATUSES,
  ARCHIVED_FULFILLMENT_STATUSES,
  HISTORY_FULFILLMENT_STATUSES,
  MARKETPLACE_FULFILLMENT_STATUS_LABELS,
  TERMINAL_FULFILLMENT_STATUSES,
  isActivePipelineFulfillmentStatus,
  marketplaceFulfillmentStatusLabel,
  requiresProductionScheduling,
  usesNewOrderHandoffEmail,
  rollupOrderFulfillmentStatus,
} from "./marketplace-fulfillment.js";

export const CreativeImportPopulationSchema = z.enum(["ARCHIVE", "LIVE"]);
export type CreativeImportPopulation = z.infer<typeof CreativeImportPopulationSchema>;

export const CreativeImportIssueCodeSchema = z.enum([
  "VENDOR_PENDING_ONBOARDING",
  "VENDOR_PRICING_MISSING",
  "SCRIPT_MISSING",
  "BATCH_MISSING",
  "CLIENT_START_DATE_MISSING",
  "CLIENT_START_DATE_PREFILLED",
  "EXPECTED_DELIVERY_DATE_MISSING",
  "UNMAPPED_COLUMN",
  "DELIVERABLE_STATUS_MISMATCH",
  "VERTICAL_FORWARD_FILLED",
]);
export type CreativeImportIssueCode = z.infer<typeof CreativeImportIssueCodeSchema>;

export const CreativeImportIssueSeveritySchema = z.enum(["BLOCKING", "WARNING"]);
export type CreativeImportIssueSeverity = z.infer<typeof CreativeImportIssueSeveritySchema>;

export const CreativeImportRowStatusSchema = z.enum(["PENDING", "COMMITTED", "BLOCKED"]);
export type CreativeImportRowStatus = z.infer<typeof CreativeImportRowStatusSchema>;

export const CreativeImportStatusSchema = z.enum([
  "PREVIEW",
  "PARTIALLY_COMMITTED",
  "COMPLETED",
  "FAILED",
]);
export type CreativeImportStatus = z.infer<typeof CreativeImportStatusSchema>;

export const CreativeImportIssueDtoSchema = z.object({
  id: z.string(),
  rowId: z.string().nullable(),
  code: CreativeImportIssueCodeSchema,
  severity: CreativeImportIssueSeveritySchema,
  message: z.string().nullable(),
});
export type CreativeImportIssueDto = z.infer<typeof CreativeImportIssueDtoSchema>;

export const CreativeImportRowDtoSchema = z.object({
  id: z.string(),
  sheetRowNumber: z.number().int(),
  status: CreativeImportRowStatusSchema,
  population: CreativeImportPopulationSchema,
  hasBrief: z.boolean(),
  vertical: z.string().nullable(),
  verticalForwardFilled: z.boolean(),
  assetName: z.string().nullable(),
  hook: z.string().nullable(),
  deliverableUrl: z.string().nullable(),
  hasDeliverable: z.boolean(),
  deliverableStatusMismatch: z.boolean(),
  vendorLabel: z.string().nullable(),
  vendorId: z.string().nullable(),
  vendorName: z.string().nullable(),
  pendingVendorOnboardingId: z.string().nullable(),
  pendingVendorContactEmail: z.string().nullable(),
  briefUrl: z.string().nullable(),
  batch: z.string().nullable(),
  opportunitySetId: z.string().nullable(),
  scriptId: z.string().nullable(),
  sheetStatus: z.string().nullable(),
  clientStartDate: z.string().nullable(),
  clientStartDatePrefilled: z.boolean(),
  expectedDeliveryDate: z.string().nullable(),
  fulfillmentStatus: MarketplaceFulfillmentStatusSchema.nullable(),
  suggestedFulfillment: MarketplaceFulfillmentStatusSchema,
  willCommit: z.boolean(),
  /** True when the row will park until a new vendor finishes onboarding. */
  willCommitFrozen: z.boolean(),
});
export type CreativeImportRowDto = z.infer<typeof CreativeImportRowDtoSchema>;

export const CreativeImportPreviewDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  status: CreativeImportStatusSchema,
  fileName: z.string().nullable(),
  rowCount: z.number().int(),
  committingRowCount: z.number().int(),
  frozenRowCount: z.number().int(),
  blockedRowCount: z.number().int(),
  blockingIssueCount: z.number().int(),
  warningIssueCount: z.number().int().optional(),
  warningsAcknowledgedAt: z.string().datetime().nullable().optional(),
  rows: z.array(CreativeImportRowDtoSchema),
  issues: z.array(CreativeImportIssueDtoSchema),
});
export type CreativeImportPreviewDto = z.infer<typeof CreativeImportPreviewDtoSchema>;

export const UpdateCreativeImportRowSchema = z.object({
  vendorId: z.string().nullable().optional(),
  hook: z.string().max(500).nullable().optional(),
  clientStartDate: z.string().date().nullable().optional(),
  expectedDeliveryDate: z.string().date().nullable().optional(),
  fulfillmentStatus: MarketplaceFulfillmentStatusSchema.nullable().optional(),
  briefUrl: z.string().url().nullable().optional(),
});
export type UpdateCreativeImportRowBody = z.infer<typeof UpdateCreativeImportRowSchema>;

export const CommitImportResultDtoSchema = z.object({
  /** Marketplace orders are created on send-to-production, not at commit. */
  createdOrderCount: z.number().int(),
  opportunitySetIds: z.array(z.string()),
  opportunityCount: z.number().int(),
  scriptCount: z.number().int(),
  pendingOrderCount: z.number().int(),
  frozenRowCount: z.number().int(),
  emailsQueued: z.number().int(),
  vendorInvitesSent: z.number().int(),
  preview: CreativeImportPreviewDtoSchema,
});
export type CommitImportResultDto = z.infer<typeof CommitImportResultDtoSchema>;

export const PendingImportOrderDtoSchema = z.object({
  id: z.string(),
  importId: z.string(),
  sheetRowNumber: z.number().int(),
  assetName: z.string().nullable(),
  vendorLabel: z.string().nullable(),
  mappedVendorId: z.string().nullable(),
  mappedVendorName: z.string().nullable(),
  clientStartDate: z.string().nullable(),
  fulfillmentStatus: MarketplaceFulfillmentStatusSchema.nullable(),
  suggestedFulfillment: MarketplaceFulfillmentStatusSchema,
  hasBrief: z.boolean(),
  sheetStatus: z.string().nullable(),
  importFileName: z.string().nullable(),
  importedAt: z.string().datetime(),
});
export type PendingImportOrderDto = z.infer<typeof PendingImportOrderDtoSchema>;

export const SendImportRowToProductionSchema = z.object({
  tenantId: z.string().min(1),
  vendorId: z.string().min(1),
});
export type SendImportRowToProductionBody = z.infer<typeof SendImportRowToProductionSchema>;

export const SendImportRowToProductionResultDtoSchema = z.object({
  orderId: z.string(),
  emailQueued: z.boolean(),
});
export type SendImportRowToProductionResultDto = z.infer<
  typeof SendImportRowToProductionResultDtoSchema
>;

export const BulkSetClientStartDateSchema = z.object({
  vendorId: z.string().optional(),
  vendorLabel: z.string().optional(),
  clientStartDate: z.string().date(),
});
export type BulkSetClientStartDateBody = z.infer<typeof BulkSetClientStartDateSchema>;

export const BulkSetVendorMappingSchema = z.object({
  vendorLabel: z.string().min(1),
  vendorId: z.string().min(1),
});
export type BulkSetVendorMappingBody = z.infer<typeof BulkSetVendorMappingSchema>;

export const BulkSetNewVendorMappingSchema = z.object({
  vendorLabel: z.string().min(1),
  contactEmail: z.string().email(),
});
export type BulkSetNewVendorMappingBody = z.infer<typeof BulkSetNewVendorMappingSchema>;
